import { Typography } from '@mui/material';
import { FunctionComponent, useState, useMemo, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  ActiveSessionsTable,
  Button,
  ConfirmationMessageModal,
  FullScreenLoader,
} from '@Components/index';
import { CommonConstants } from '@Constants/index';
import { ActiveSessionsQuery } from '@Models/report.model';
import {
  useGetActiveSessionsQuery,
  useEndSessionMutation,
} from '@Services/report.service';
import { useGetBleedOutStateQuery } from '@Services/settings.service';
import { ConfirmationActionTypes } from '@Typings/global.types';
import query from '@Utils/common/query.utils';
import { getActiveSessionsRTKQuery } from '@Utils/report/report.utils';

import { GetRows } from './ActiveSessions.utils';

const ActiveSessions: FunctionComponent = (): JSX.Element => {
  /** HOOKS */
  const location = useLocation();
  const { t } = useTranslation();

  /** STATES */
  const [checked, setChecked] = useState<readonly string[]>([]);
  const [sessionId, setSessionId] = useState<string>('');
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isBleedOutActive, setIsBleedOutActive] = useState<boolean>(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationActionType, setConfirmationActionType] =
    useState<ConfirmationActionTypes | null>(null);
  const [confirmationMessage, setConfirmationMessage] =
    useState<React.ReactNode | null>(null);
  const [isBulkAction, setIsBulkAction] = useState(false);

  const activeSessionsQuery: ActiveSessionsQuery = useMemo(
    () => getActiveSessionsRTKQuery(location.search),
    [location.search]
  );

  /** RTK QUERY HOOK */
  const { data, isLoading, isFetching, refetch } = useGetActiveSessionsQuery(
    activeSessionsQuery,
    { skip: !query.get(CommonConstants.QUERY_PARAMS.REPORT.REPORT_TYPE) }
  );

  const {
    data: dataBleedOutState,
    isLoading: isLoadingBleedOutState,
    isFetching: isFetchingBleedOutState,
  } = useGetBleedOutStateQuery();

  /** MUTATIONS */
  const [endSessions, endSessionsMethods] = useEndSessionMutation();

  const handleEndSession = (ids?: readonly string[]) => endSessions({ ids });

  /** TABLE LOADING STATE */
  const loading =
    isLoading ||
    isFetching ||
    isLoadingBleedOutState ||
    isFetchingBleedOutState ||
    endSessionsMethods.isLoading;

  useEffect(() => {
    if (dataBleedOutState !== undefined) {
      setIsBleedOutActive(dataBleedOutState.isActive);
    }
  }, [dataBleedOutState]);

  /** Updates Last Updated info whenever data is fetched */
  useEffect(() => {
    if (data && !isFetching) {
      setLastUpdated(new Date());
    }
  }, [data, isFetching]);

  /** UPDATES THE CHECKED STATE AFTER BULK ACTIONS */
  useEffect(() => {
    if (endSessionsMethods.data) {
      setItemsCount(0);
      setChecked([]);
      setIsBulkAction(false);
    }
  }, [endSessionsMethods.data]);

  /** Handles Bulk end session button click */
  const endSessionBtnPropsOnClick = () => {
    setIsConfirmationModalOpen(true);
    setIsBulkAction(true);
    setConfirmationActionType(ConfirmationActionTypes.EndSession);

    const message = (
      <Typography>
        <Trans
          i18nKey="common.confirmationMessages.Confirm bulk end"
          components={{ strong: <strong /> }}
        />
      </Typography>
    );
    setConfirmationMessage(message);
  };

  /** Handles confirmation click of modal and performs action accordingly */
  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    if (confirmationActionType === ConfirmationActionTypes.EndSession) {
      return isBulkAction
        ? handleEndSession(checked)
        : handleEndSession([sessionId]);
    }
  };

  /** Table Actions Component Function
   *
   * Returns the table actions component with required Button.
   */
  const actionsComponent = (id: string, accountName: string) => {
    /** Handles click of action menu button */
    const handleTableActionsClick = (
      confirmationType: ConfirmationActionTypes
    ) => {
      setIsConfirmationModalOpen(true);
      setConfirmationActionType(confirmationType);
      const message = (
        <Typography
          variant="main"
          sx={{
            '& .accountName': {
              color: 'secondary.main',
            },
          }}
        >
          <Trans
            values={{ accountName }}
            i18nKey="common.confirmationMessages.Confirm End Session"
            components={{
              strong: <strong />,
              span: <span className="accountName" />,
            }}
          />
        </Typography>
      );
      setConfirmationMessage(message);
    };

    return (
      <Button
        variant="outlined"
        text={t('reports.End')}
        sx={{ minWidth: '7.2rem' }}
        color="error"
        onClick={() => {
          setSessionId(id);
          handleTableActionsClick(ConfirmationActionTypes.EndSession);
        }}
      />
    );
  };

  /** Rows, Total Rows Count */
  const rows = GetRows(data, actionsComponent);
  const totalRowsCount =
    (data && data.length && parseInt(data[0].fullCount, 10)) ||
    CommonConstants.INITIAL_ROWS_PER_PAGE;

  /** Handle Bulk Count
   * Callback to update ItemsCount
   */
  const handleBulk = (count: number) => setItemsCount(count);

  return !isLoading ? (
    <>
      <ConfirmationMessageModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={confirmationMessage}
        onClick={handleConfirmationModalClick}
      />
      <ActiveSessionsTable
        selectedBtnNumber={itemsCount || checked.length}
        endSessionBtnProps={{
          onClick: () => endSessionBtnPropsOnClick(),
          disabled: loading,
        }}
        refreshBtnProps={{
          onClick: refetch,
          disabled: loading,
        }}
        tableProps={{
          loading,
          rows,
          checked,
          setChecked,
          paginationProps: {
            count: totalRowsCount,
          },
        }}
        isBleedOutActive={isBleedOutActive}
        lastUpdated={lastUpdated}
        setLastUpdated={setLastUpdated}
        handleBulk={handleBulk}
      />
    </>
  ) : (
    <FullScreenLoader />
  );
};

export default ActiveSessions;
